import { MessageResponse } from '@crowdex/ui/ai/message';
import { readableReferences } from '@crowdex/ui/lib/inline-reference';
import { Globe2, MessageSquare, MousePointerClick } from 'lucide-react';
import { parseElementSelectionText } from '@/lib/conversations/message-part/element-selection';
import { elementSelectionDetail, isWebSelection } from '@/lib/element-selection';
import { AnnotationChip } from './annotation-chip';
import { useSmoothStreamingText } from './use-smooth-streaming-text';
import { REPLY_MARKDOWN } from './workspace-image';

export function ElementSelectionText({
  isStreaming,
  text,
}: {
  isStreaming?: boolean;
  text: string;
}) {
  const visibleStreamText = useSmoothStreamingText(text, isStreaming === true);
  const { annotations, selections, visibleText } = parseElementSelectionText(visibleStreamText);
  // A composer reference reads as its label: markdown would block its link.
  const readableText = readableReferences(visibleText, 'markdown');

  return (
    <>
      {visibleText.length > 0 && (
        // Streamed, its blocks follow the text in a transition; done, they render at once: a
        // transition never commits while the page keeps rendering (a call's voice level).
        <MessageResponse
          className="text-foreground/95"
          // Its images: a workspace file through the conversation's file space, else remote.
          {...REPLY_MARKDOWN}
          mode={isStreaming === true ? 'streaming' : 'static'}
        >
          {readableText}
        </MessageResponse>
      )}
      {selections.length > 0 && (
        <span className="flex flex-wrap gap-2">
          {selections.map((selection, index) => {
            const summary = elementSelectionDetail(selection);
            const SourceIcon = isWebSelection(selection) ? Globe2 : MousePointerClick;
            return (
              <AnnotationChip
                content={
                  <div className="space-y-2.5">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs">
                      <SourceIcon className="size-3.5 shrink-0" />
                      <span className="truncate">{summary.source}</span>
                    </div>
                    <p className="line-clamp-2 font-medium text-sm leading-5">{summary.target}</p>
                    {summary.annotation !== null && (
                      <p className="line-clamp-3 rounded-lg bg-muted/70 px-2.5 py-2 text-muted-foreground text-xs leading-5">
                        {summary.annotation}
                      </p>
                    )}
                  </div>
                }
                icon={SourceIcon}
                // biome-ignore lint/suspicious/noArrayIndexKey: A message can point at the same element twice; selections are positions in it.
                key={`${index}-${selection.slice(0, 48)}`}
              >
                Annotation {index + 1}
              </AnnotationChip>
            );
          })}
        </span>
      )}
      {annotations.length > 0 && (
        <span className="flex flex-wrap gap-2">
          {annotations.map((annotation, index) => (
            <AnnotationChip
              content={
                <div className="space-y-2.5">
                  <p className="line-clamp-3 whitespace-pre-wrap font-medium text-sm leading-5">
                    {annotation.text}
                  </p>
                  <p className="line-clamp-3 rounded-lg bg-muted/70 px-2.5 py-2 text-muted-foreground text-xs leading-5">
                    {annotation.annotation}
                  </p>
                </div>
              }
              icon={MessageSquare}
              // biome-ignore lint/suspicious/noArrayIndexKey: A message can quote the same text twice; annotations are positions in it.
              key={`${index}-${annotation.text.slice(0, 48)}`}
            >
              Annotation {index + 1}
            </AnnotationChip>
          ))}
        </span>
      )}
    </>
  );
}
